import { createContext, useContext, useState, useCallback } from "react";
import { selectNodeAndHideFloating } from "@/tiptap/hooks/use-floating-toolbar-visibility"
import { scrollHeadingIntoTocView, getTocHeadingElement } from "@/tiptap/nodes/toc-node/lib/toc-viewport"

export { getScrollableAncestor } from "@/tiptap/nodes/toc-node/lib/toc-viewport"

const TocContext = createContext(undefined)

/**
 * Normalizes heading depths for a table of contents (TOC) structure.
 *
 * This function ensures proper hierarchical nesting where a heading can only be
 * a child of a previous heading with a smaller level number (higher priority).
 * It prevents incorrect structures like h2 being listed under h3.
 *
 * Algorithm:
 * 1. Rebases all levels so the minimum level becomes 1 (root level)
 * 2. Maintains a stack of ancestors; pops entries until a smaller level is found
 * 3. If found, nests it under that parent (parent depth + 1)
 * 4. If not found, treats it as a root-level item (depth = 1)
 *
 * @param items - Array of heading items with `level` or `originalLevel` properties
 * @returns Array of normalized depths corresponding to each heading item
 */
export function normalizeHeadingDepths(items) {
  if (items.length === 0) return []

  const raw = items.map((h) => h.originalLevel ?? h.level ?? 1)

  const positives = raw.filter((l) => l > 0)
  const root = positives.includes(1) ? 1 : Math.min(...positives)

  const lvl = raw.map((l) => Math.max(1, l - (root - 1)))

  const depths = []
  const stack = []

  for (let i = 0; i < lvl.length; i++) {
    const currentLevel = lvl[i] ?? 1

    // Pop until we find a valid parent (strictly smaller level)
    while (stack.length > 0 && stack[stack.length - 1].level >= currentLevel) {
      stack.pop()
    }

    const depth = stack.length === 0 ? 1 : stack[stack.length - 1].depth + 1

    depths.push(depth)

    stack.push({ level: currentLevel, depth })
  }

  return depths
}

/**
 * Low-level navigate helper (not exported in context directly).
 * Form editors scroll only the editor pane; KB articles still use the window.
 */
const doNavigateToHeading = (
  item,
  topOffset,
  behavior = "smooth"
) => {
  if (typeof window === "undefined") return

  const element = getTocHeadingElement(item)
  if (!element) return

  scrollHeadingIntoTocView(element, { topOffset, behavior })

  if (item.editor && typeof item.pos === "number") {
    selectNodeAndHideFloating(item.editor, item.pos)
  }

  if (item.id) {
    const url = new URL(window.location.href)
    url.hash = item.id
    window.history.replaceState(null, "", url.toString())
  }
}

export const TocProvider = ({
  children
}) => {
  const [tocContent, setTocContent] = useState(null)

  const navigateToHeading = useCallback((item, options) => {
    const topOffset = options?.topOffset ?? 0
    const behavior = options?.behavior ?? "smooth"
    doNavigateToHeading(item, topOffset, behavior)
  }, [])

  return (
    <TocContext.Provider
      value={{
        tocContent,
        setTocContent,
        navigateToHeading,
        normalizeHeadingDepths,
      }}>
      {children}
    </TocContext.Provider>
  );
}

export const useToc = () => {
  const ctx = useContext(TocContext)
  if (!ctx) {
    throw new Error("useToc must be used inside <TocProvider>")
  }
  return ctx
}
